import React from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../util/types';

// Import shared components
import { ListingLink } from '../../components';
import { EditLiveRoomBehindTheScenesForm } from '../../forms';

// Import modules from this directory
import css from './EditLiveRoomBehindTheScenesPanel.module.css';

const getInitialValues = props => {
  const { images, videos } = props;
  return { images, videos };
};

// This function denormalizes the video data by removing the 'file' and 'videoId' properties
// and retaining only the attributes that should be saved within a listing.
// The resulting array of videos is then sent to the API to be added to the publicData of a listing.
const denormaliseVideos = videos =>
  videos ? videos.map(({ file, videoId, ...video }) => video) : [];

const EditLiveRoomBehindTheScenesPanel = props => {
  const {
    className,
    rootClassName,
    errors,
    disabled,
    ready,
    listing,
    onVideoUpload,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    showListingInProgress,
    onSubmit,
    onRemoveVideo,
    onGetPrevPathParams,
  } = props;

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);

  const isPublished = listing.id && listing.attributes.state !== LISTING_STATE_DRAFT;

  // videos title
  const unpublishedPanelTitle = (
    <FormattedMessage id="EditLiveRoomBehindTheScenesPanel.createListingVideosTitle" />
  );
  const publishedPanelTitle = (
    <FormattedMessage
      id="EditLiveRoomBehindTheScenesPanel.videosTitle"
      values={{
        listingTitle: <ListingLink className={css.listingLink} listing={listing} isLiveRoom />,
      }}
    />
  );

  const panelTitle = isPublished ? publishedPanelTitle : unpublishedPanelTitle;

  return (
    <div className={classes}>
      <EditLiveRoomBehindTheScenesForm
        className={css.form}
        disabled={disabled}
        ready={ready}
        fetchErrors={errors}
        panelTitle={panelTitle}
        initialValues={getInitialValues(props)}
        onVideoUpload={onVideoUpload}
        onSubmit={values => {
          const { videos } = values;
          const updatedValues = {
            publicData: {
              behindTheScenes: denormaliseVideos(videos),
              completedBehindTheScenesTab: true,
            },
          };
          onSubmit(updatedValues);
        }}
        onRemoveVideo={onRemoveVideo}
        onGetPrevPathParams={onGetPrevPathParams}
        saveActionMsg={submitButtonText}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        showListingInProgress={showListingInProgress}
        listing={listing}
      />
    </div>
  );
};

EditLiveRoomBehindTheScenesPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  images: [],
  listing: null,
  showListingInProgress: false,
};

EditLiveRoomBehindTheScenesPanel.propTypes = {
  className: string,
  rootClassName: string,
  errors: object,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updateInProgress: bool.isRequired,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  onVideoUpload: func.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  showListingInProgress: bool.isRequired,
  onRemoveVideo: func.isRequired,
  onGetPrevPathParams: func.isRequired,
};

export default EditLiveRoomBehindTheScenesPanel;
